import React, { useMemo } from "react";

function getCategory(node) {
  return String(node?.category || node?.definition?.category || "").toLowerCase();
}

function isPanelNode(node) {
  const category = getCategory(node);

  return (
    category.startsWith("board.") ||
    category.startsWith("front.") ||
    category.includes("panel")
  );
}

function getPanelSide(node) {
  return (
    node?.partitionSide ||
    node?.role ||
    node?.generatedRole ||
    node?.panelRole ||
    "front"
  );
}

function parseValue(value) {
  if (value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? n : value;
}

function readJson(value, fallback = {}) {
  if (!value) return fallback;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function getRunnerParamsFromDefinition(definition) {
  const defaults = readJson(
    definition?.default_parameters_json ||
      definition?.defaultParametersJson ||
      definition?.defaultParameters ||
      {},
    {}
  );

  const geometryDefaults = readJson(
    definition?.geometry_defaults_json ||
      definition?.geometryDefaultsJson ||
      definition?.geometryDefaults ||
      {},
    {}
  );

  const linkedItem =
    definition?.catalogItem ||
    definition?.catalog_item ||
    definition?.familyItem ||
    definition?.family_item ||
    definition?.linkedItem ||
    definition?.linked_item ||
    null;

  const attrs = readJson(
    linkedItem?.attributes_json || linkedItem?.attributesJson || {},
    {}
  );

  const technical = attrs.technical || {};
  const assembly = attrs.assembly_parameters || {};

  const runnerLength =
    Number(assembly.runnerLength) ||
    Number(defaults.runnerLength) ||
    Number(geometryDefaults.runnerLength) ||
    Number(technical.nominalLengthMm) ||
    Number(technical.drawerLengthMm) ||
    300;

  const drawerDepth =
    Number(assembly.drawerDepth) ||
    Number(defaults.drawerDepth) ||
    Number(geometryDefaults.drawerDepth) ||
    Number(technical.drawerLengthMm) ||
    Number(runnerLength) ||
    300;

  const runnerHeight =
    Number(assembly.runnerHeight) ||
    Number(defaults.runnerHeight) ||
    Number(geometryDefaults.runnerHeight) ||
    Number(technical.shankHeightOfCabinetProfileMm) ||
    40;

  const sideClearance =
    Number(assembly.sideClearance) ||
    Number(defaults.sideClearance) ||
    Number(geometryDefaults.sideClearance) ||
    7.5;

  const minimumCabinetDepth =
    Number(assembly.minimumCabinetDepth) ||
    Number(defaults.minimumCabinetDepth) ||
    Number(technical.minimumInternalCabinetDepthMm) ||
    null;

  const drawerDepthReduction =
    Number(assembly.drawerDepthReduction) ||
    Number(defaults.drawerDepthReduction) ||
    Math.max(0, runnerLength - drawerDepth) ||
    10;

  return {
    runnerLength,
    drawerDepth,
    runnerHeight,
    sideClearance,
    minimumCabinetDepth,
    drawerDepthReduction,
  };
}

function isHardwareDefinition(definition, kind) {
  const category = String(definition?.category || "").toLowerCase();
  const nodeType = String(
    definition?.nodeType || definition?.node_type || ""
  ).toLowerCase();

  const code = String(definition?.code || "").toLowerCase();
  const name = String(definition?.name || "").toLowerCase();

  const geometryType = String(
    definition?.geometryType || definition?.geometry_type || ""
  ).toLowerCase();

  const text = `${category} ${nodeType} ${code} ${name} ${geometryType}`;

  const looksLikeHardware =
    nodeType === "hardware" ||
    category.includes("hardware") ||
    category.includes("hinge") ||
    category.includes("handle") ||
    category.includes("lift") ||
    category.includes("runner") ||
    geometryType === "catalog_glb";

  if (!looksLikeHardware) return false;

  if (kind === "handle") {
    return (
      text.includes("handle") ||
      text.includes("ruck") ||
      text.includes("ruč") ||
      text.includes("ruc") ||
      text.includes("knob") ||
      text.includes("pull")
    );
  }

  if (kind === "hinge") {
    return (
      text.includes("hinge") ||
      text.includes("sarka") ||
      text.includes("šarka") ||
      text.includes("blumotion") ||
      text.includes("clip")
    );
  }

  if (kind === "lift") {
    return (
      text.includes("lift") ||
      text.includes("flap") ||
      text.includes("stay") ||
      text.includes("aventos")
    );
  }

  if (kind === "runner") {
    return (
      text.includes("runner") ||
      text.includes("drawer") ||
      text.includes("kliza") ||
      text.includes("tandem") ||
      text.includes("movento")
    );
  }

  return looksLikeHardware;
}

function getDefinitionGroup(definition) {
  const category = String(definition?.category || "").toLowerCase();

  if (category.startsWith("front.")) return "Front panels";
  if (category.startsWith("glass.")) return "Glass / mirror";
  if (category.startsWith("mirror.")) return "Glass / mirror";
  if (category.startsWith("board.")) return "Boards";
  if (category.includes("frame")) return "Framed panels";
  if (category.includes("panel")) return "Panels";

  return "Other";
}

function isDefinitionCompatibleWithPanel(definition, side) {
  const category = String(definition?.category || "").toLowerCase();
  const nodeType = String(definition?.nodeType || definition?.node_type || "").toLowerCase();

  if (nodeType && nodeType !== "element") return false;

  const isPanelDefinition =
    category.startsWith("board.") ||
    category.startsWith("front.") ||
    category.startsWith("glass.") ||
    category.startsWith("mirror.") ||
    category.includes("panel") ||
    category.includes("frame");

  if (!isPanelDefinition) return false;

  const s = String(side || "").toLowerCase();

  if (s === "front") {
    return (
      category.startsWith("front.") ||
      category.startsWith("board.") ||
      category.startsWith("glass.") ||
      category.startsWith("mirror.") ||
      category.includes("panel") ||
      category.includes("frame")
    );
  }

  if (["left", "right", "side"].includes(s)) {
    return (
      category.startsWith("board.") ||
      category.startsWith("glass.") ||
      category.startsWith("mirror.") ||
      category.includes("panel") ||
      category.includes("frame")
    );
  }

  if (["back", "top", "bottom", "shelf"].includes(s)) {
    return (
      category.startsWith("board.") ||
      category.startsWith("glass.") ||
      category.startsWith("mirror.") ||
      category.includes("panel")
    );
  }

  return isPanelDefinition;
}

function DefinitionOptionGroup({ title, items }) {
  if (!items.length) return null;

  return (
    <optgroup label={title}>
      {items.map((def) => (
        <option key={def.id} value={def.id}>
          {def.name || def.code || `Definition ${def.id}`} · {def.category}
        </option>
      ))}
    </optgroup>
  );
}

export default function SelectedPanelSimplePanel({
  selectedNode,
  resolvedNode,
  onChangeNode,
  definitions = [],
}) {
  const node = selectedNode || resolvedNode;
  const resolved = resolvedNode || node;

  if (!node || !isPanelNode(resolved)) {
    return null;
  }

  const panelSide = getPanelSide(resolved);
  const effective = resolved?.effectiveParameters || {};
  const overrides = node.parameterOverrides || {};

  const parentId = resolved?.parentGeneratedFromId;
const side = resolved?.partitionSide || resolved?.role || panelSide || "front";
const panelKey = resolved?.panelKey;

const parentPartitions = resolved?.parentPartitions || {};
const sidePartition = parentPartitions?.[side] || {
  horizontal: [],
  vertical: [],
  panelOverrides: {},
};

const currentPanelOverride =
  parentId && panelKey
    ? sidePartition?.panelOverrides?.[panelKey] || {}
    : {};

const currentDefinitionId =
  currentPanelOverride.definitionId || node.definitionId || resolved?.definitionId || "";

const currentPanelBehavior =
  currentPanelOverride.panelBehavior || node.panelBehavior || "auto";

const currentHardwareMode =
  currentPanelOverride.hardwareMode || node.hardwareMode || "auto";

const currentShowHandle =
  currentPanelOverride.showHandle !== undefined
    ? currentPanelOverride.showHandle
    : node.showHandle !== false;

const currentHandleDefinitionId =
  currentPanelOverride.handleDefinitionId || node.handleDefinitionId || "";

const currentHandlePlacementMode =
  currentPanelOverride.handlePlacementMode ||
  currentPanelOverride.parameterOverrides?.handlePlacementMode ||
  "auto";

const currentHandleOffsetX =
  currentPanelOverride.handleOffsetX ??
  currentPanelOverride.parameterOverrides?.handleOffsetX ??
  "";

const currentHandleOffsetY =
  currentPanelOverride.handleOffsetY ??
  currentPanelOverride.parameterOverrides?.handleOffsetY ??
  "";

const currentHandleOffsetZ =
  currentPanelOverride.handleOffsetZ ??
  currentPanelOverride.parameterOverrides?.handleOffsetZ ??
  "";

const currentHandleRotationX =
  currentPanelOverride.handleRotationX ??
  currentPanelOverride.parameterOverrides?.handleRotationX ??
  "";

const currentHandleRotationY =
  currentPanelOverride.handleRotationY ??
  currentPanelOverride.parameterOverrides?.handleRotationY ??
  "";

const currentHandleRotationZ =
  currentPanelOverride.handleRotationZ ??
  currentPanelOverride.parameterOverrides?.handleRotationZ ??
  "";

const currentHingeDefinitionId =
  currentPanelOverride.hingeDefinitionId || node.hingeDefinitionId || "";

const currentLiftDefinitionId =
  currentPanelOverride.liftDefinitionId || node.liftDefinitionId || "";

  const compatibleDefinitions = useMemo(() => {
    return (definitions || [])
      .filter((def) => isDefinitionCompatibleWithPanel(def, panelSide))
      .sort((a, b) => {
        const ga = getDefinitionGroup(a);
        const gb = getDefinitionGroup(b);

        if (ga !== gb) return ga.localeCompare(gb);

        return String(a.name || a.code || "").localeCompare(
          String(b.name || b.code || "")
        );
      });
  }, [definitions, panelSide]);

  const grouped = useMemo(() => {
    const map = new Map();

    compatibleDefinitions.forEach((def) => {
      const group = getDefinitionGroup(def);
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(def);
    });

    return Array.from(map.entries()).map(([title, items]) => ({
      title,
      items,
    }));
  }, [compatibleDefinitions]);

  const handleDefinitions = useMemo(
  () => (definitions || []).filter((def) => isHardwareDefinition(def, "handle")),
  [definitions]
);

const hingeDefinitions = useMemo(
  () => (definitions || []).filter((def) => isHardwareDefinition(def, "hinge")),
  [definitions]
);

const liftDefinitions = useMemo(
  () => (definitions || []).filter((def) => isHardwareDefinition(def, "lift")),
  [definitions]
);

const runnerDefinitions = useMemo(
  () => (definitions || []).filter((def) => isHardwareDefinition(def, "runner")),
  [definitions]
);

const currentDrawerRunnerDefinitionId =
  currentPanelOverride.drawerRunnerDefinitionId ||
  node.drawerRunnerDefinitionId ||
  "";

  function patchNode(patch) {
    onChangeNode?.({
      id: node.id,
      ...patch,
    });
  }

  function patchPanelOverride(patch) {
  if (parentId && panelKey) {
    const previousOverrides = sidePartition.panelOverrides || {};
    const previousPanelOverride = previousOverrides[panelKey] || {};

    onChangeNode?.({
      id: parentId,
      partitions: {
        ...parentPartitions,
        [side]: {
          ...sidePartition,
          panelOverrides: {
            ...previousOverrides,
            [panelKey]: {
              ...previousPanelOverride,
              ...patch,
            },
          },
        },
      },
    });

    return;
  }

  patchNode(patch);
}

  function updateParam(key, value) {
  const nextValue = parseValue(value);

  if (parentId && panelKey) {
    patchPanelOverride({
      parameterOverrides: {
        ...(currentPanelOverride.parameterOverrides || {}),
        [key]: nextValue,
      },
    });

    return;
  }

  patchNode({
    parameterOverrides: {
      ...(node.parameterOverrides || {}),
      [key]: nextValue,
    },
  });
}

function updateDrawerParam(key, value) {
  const nextValue = parseValue(value);

  if (parentId && panelKey) {
    patchPanelOverride({
      [key]: nextValue,

      parameterOverrides: {
        ...(currentPanelOverride.parameterOverrides || {}),
        [key]: nextValue,
      },
    });

    return;
  }

  patchNode({
    [key]: nextValue,

    parameterOverrides: {
      ...(node.parameterOverrides || {}),
      [key]: nextValue,
    },
  });
}

function updateHandlePlacementParam(key, value) {
  const nextValue = parseValue(value);

  patchPanelOverride({
    [key]: nextValue,

    parameterOverrides: {
      ...(currentPanelOverride.parameterOverrides || {}),
      [key]: nextValue,
    },
  });
}

function applyHandlePreset(type) {
  if (type === "door_vertical") {
    patchPanelOverride({
      handlePlacementMode: "custom",
      handleRotationX: 0,
      handleRotationY: 0,
      handleRotationZ: 90,

      parameterOverrides: {
        ...(currentPanelOverride.parameterOverrides || {}),
        handlePlacementMode: "custom",
        handleRotationX: 0,
        handleRotationY: 0,
        handleRotationZ: 90,
      },
    });

    return;
  }

  if (type === "drawer_horizontal") {
    patchPanelOverride({
      handlePlacementMode: "custom",
      handleRotationX: 0,
      handleRotationY: 0,
      handleRotationZ: 0,

      parameterOverrides: {
        ...(currentPanelOverride.parameterOverrides || {}),
        handlePlacementMode: "custom",
        handleRotationX: 0,
        handleRotationY: 0,
        handleRotationZ: 0,
      },
    });

    return;
  }

  if (type === "auto") {
    patchPanelOverride({
      handlePlacementMode: "auto",
      handleOffsetX: null,
      handleOffsetY: null,
      handleOffsetZ: null,
      handleRotationX: null,
      handleRotationY: null,
      handleRotationZ: null,

      parameterOverrides: {
        ...(currentPanelOverride.parameterOverrides || {}),
        handlePlacementMode: "auto",
        handleOffsetX: null,
        handleOffsetY: null,
        handleOffsetZ: null,
        handleRotationX: null,
        handleRotationY: null,
        handleRotationZ: null,
      },
    });
  }
}

 function changeDefinition(definitionId) {
  if (!definitionId) return;

  patchPanelOverride({
    definitionId: Number(definitionId),
  });
}

const drawerParams = {
  ...currentPanelOverride,
  ...(currentPanelOverride.parameterOverrides || {}),
};

function changeDrawerRunner(definitionId) {
  if (!definitionId) {
    patchPanelOverride({
      drawerRunnerDefinitionId: null,
      panelBehavior: currentPanelBehavior,
      hardwareMode: currentHardwareMode,
    });
    return;
  }

  const selectedRunner = runnerDefinitions.find(
    (def) => String(def.id) === String(definitionId)
  );

  const runnerParams = getRunnerParamsFromDefinition(selectedRunner);

  patchPanelOverride({
  drawerRunnerDefinitionId: Number(definitionId),
  panelBehavior: "drawer",
  hardwareMode: "drawer",

  // Direktno na panel override
  runnerLength: runnerParams.runnerLength,
  drawerDepth: runnerParams.drawerDepth,
  sideClearance: runnerParams.sideClearance,
  runnerHeight: runnerParams.runnerHeight,
  minimumCabinetDepth: runnerParams.minimumCabinetDepth,
  drawerDepthReduction: runnerParams.drawerDepthReduction,

  // I u parameterOverrides
  parameterOverrides: {
    ...(currentPanelOverride.parameterOverrides || {}),
    runnerLength: runnerParams.runnerLength,
    drawerDepth: runnerParams.drawerDepth,
    sideClearance: runnerParams.sideClearance,
    runnerHeight: runnerParams.runnerHeight,
    minimumCabinetDepth: runnerParams.minimumCabinetDepth,
    drawerDepthReduction: runnerParams.drawerDepthReduction,
  },
});
}

  return (
    <div className="border rounded p-3 bg-white space-y-3">
      <div>
        <div className="font-semibold text-sm">Selected panel</div>
        <div className="text-xs text-slate-500">
          Zamena izabranog panela definicijom iz biblioteke.
        </div>
      </div>

      <div className="rounded border bg-slate-50 p-2 text-xs text-slate-600">
        <div>
          <strong>Panel:</strong> {resolved?.name || node.id}
        </div>
        <div>
          <strong>Side:</strong> {panelSide}
        </div>
        <div>
          <strong>Current:</strong>{" "}
          {resolved?.definition?.name || resolved?.definitionId || node.definitionId}
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-500">Replace panel with</label>
        <select
          value={currentDefinitionId || ""}
          onChange={(e) => changeDefinition(e.target.value)}
          className="w-full px-2 py-1.5 border rounded text-sm bg-white"
        >
          <option value="">Select definition...</option>

          {grouped.map((group) => (
            <DefinitionOptionGroup
              key={group.title}
              title={group.title}
              items={group.items}
            />
          ))}
        </select>

        {!compatibleDefinitions.length && (
          <div className="mt-1 text-xs text-amber-600">
            Nema kompatibilnih panel definicija u biblioteci.
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-slate-500">Panel behavior</label>
          <select
  value={currentPanelBehavior}
  onChange={(e) =>
    patchPanelOverride({
      panelBehavior: e.target.value,
    })
  }
  className="w-full px-2 py-1.5 border rounded text-sm bg-white"
>
  <option value="auto">Auto</option>
  <option value="fixed">Fixed</option>
  <option value="door">Door</option>
  <option value="drawer">Drawer</option>
  <option value="decorative">Decorative</option>
  <option value="insert">Insert</option>
</select>
        </div>

        <div>
          <label className="text-xs text-slate-500">Hardware</label>
          <select
  value={currentHardwareMode}
  onChange={(e) =>
    patchPanelOverride({
      hardwareMode: e.target.value,
    })
  }
  className="w-full px-2 py-1.5 border rounded text-sm bg-white"
>
  <option value="auto">Auto</option>
  <option value="none">None</option>
  <option value="hinged_left">Hinged left</option>
  <option value="hinged_right">Hinged right</option>
  <option value="lift">Lift</option>
  <option value="drawer">Drawer</option>
  <option value="fixed">Fixed</option>
</select>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={currentShowHandle !== false}
onChange={(e) =>
  patchPanelOverride({
    showHandle: e.target.checked,
  })
}
        />
        Show handle
      </label>

      <div className="border-t pt-3 space-y-2">
  <div className="text-xs font-semibold text-slate-500">
    Hardware definitions
  </div>

  <div className="text-[11px] text-slate-400">
    Handles: {handleDefinitions.length} · Hinges: {hingeDefinitions.length} ·
    Lifts: {liftDefinitions.length}
  </div>

  <div>
    <label className="text-xs text-slate-500">Handle / ručkica</label>
    <select
      value={currentHandleDefinitionId || ""}
      onChange={(e) =>
        patchPanelOverride({
          handleDefinitionId: e.target.value ? Number(e.target.value) : null,
          showHandle: Boolean(e.target.value),
        })
      }
      className="w-full px-2 py-1.5 border rounded text-sm bg-white"
    >
      <option value="">No handle / Auto</option>
      {handleDefinitions.map((def) => (
        <option key={def.id} value={def.id}>
          {def.name || def.code} · {def.category}
        </option>
      ))}
    </select>
  </div>

  {currentHandleDefinitionId ? (
  <div className="border rounded p-2 bg-slate-50 space-y-2">
    <div className="flex items-center justify-between gap-2">
      <div>
        <div className="text-xs font-semibold text-slate-500">
          Handle placement
        </div>
        <div className="text-[11px] text-slate-400">
          Auto: door = vertical, drawer = horizontal. Custom allows manual correction.
        </div>
      </div>

      <button
        type="button"
        onClick={() => applyHandlePreset("auto")}
        className="px-2 py-1 border rounded bg-white text-xs text-blue-700 hover:bg-blue-50"
      >
        Reset auto
      </button>
    </div>

    <div>
      <label className="text-xs text-slate-500">Placement mode</label>
      <select
        value={currentHandlePlacementMode}
        onChange={(e) =>
          updateHandlePlacementParam("handlePlacementMode", e.target.value)
        }
        className="w-full px-2 py-1.5 border rounded text-sm bg-white"
      >
        <option value="auto">Auto</option>
        <option value="custom">Custom</option>
      </select>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <button
        type="button"
        onClick={() => applyHandlePreset("door_vertical")}
        className="px-2 py-1.5 border rounded bg-white text-xs hover:bg-slate-100"
      >
        Vertical preset
      </button>

      <button
        type="button"
        onClick={() => applyHandlePreset("drawer_horizontal")}
        className="px-2 py-1.5 border rounded bg-white text-xs hover:bg-slate-100"
      >
        Horizontal preset
      </button>
    </div>

    <div className="grid grid-cols-3 gap-2">
      <div>
        <label className="text-xs text-slate-500">Pos X</label>
        <input
          type="number"
          value={currentHandleOffsetX}
          placeholder="auto"
          onChange={(e) =>
            updateHandlePlacementParam("handleOffsetX", e.target.value)
          }
          className="w-full px-2 py-1 border rounded text-sm"
        />
      </div>

      <div>
        <label className="text-xs text-slate-500">Pos Y</label>
        <input
          type="number"
          value={currentHandleOffsetY}
          placeholder="auto"
          onChange={(e) =>
            updateHandlePlacementParam("handleOffsetY", e.target.value)
          }
          className="w-full px-2 py-1 border rounded text-sm"
        />
      </div>

      <div>
        <label className="text-xs text-slate-500">Pos Z</label>
        <input
          type="number"
          value={currentHandleOffsetZ}
          placeholder="auto"
          onChange={(e) =>
            updateHandlePlacementParam("handleOffsetZ", e.target.value)
          }
          className="w-full px-2 py-1 border rounded text-sm"
        />
      </div>
    </div>

    <div className="grid grid-cols-3 gap-2">
      <div>
        <label className="text-xs text-slate-500">Rot X</label>
        <input
          type="number"
          value={currentHandleRotationX}
          placeholder="auto"
          onChange={(e) =>
            updateHandlePlacementParam("handleRotationX", e.target.value)
          }
          className="w-full px-2 py-1 border rounded text-sm"
        />
      </div>

      <div>
        <label className="text-xs text-slate-500">Rot Y</label>
        <input
          type="number"
          value={currentHandleRotationY}
          placeholder="auto"
          onChange={(e) =>
            updateHandlePlacementParam("handleRotationY", e.target.value)
          }
          className="w-full px-2 py-1 border rounded text-sm"
        />
      </div>

      <div>
        <label className="text-xs text-slate-500">Rot Z</label>
        <input
          type="number"
          value={currentHandleRotationZ}
          placeholder="auto"
          onChange={(e) =>
            updateHandlePlacementParam("handleRotationZ", e.target.value)
          }
          className="w-full px-2 py-1 border rounded text-sm"
        />
      </div>
    </div>
  </div>
) : null}

  <div>
    <label className="text-xs text-slate-500">Hinge / šarka</label>
    <select
      value={currentHingeDefinitionId || ""}
      onChange={(e) =>
  patchPanelOverride({
    hingeDefinitionId: e.target.value ? Number(e.target.value) : null,
  })
}
      className="w-full px-2 py-1.5 border rounded text-sm bg-white"
    >
      <option value="">No hinge / Auto</option>
      {hingeDefinitions.map((def) => (
        <option key={def.id} value={def.id}>
          {def.name || def.code} · {def.category}
        </option>
      ))}
    </select>
  </div>

  <div>
    <label className="text-xs text-slate-500">Lift</label>
    <select
      value={currentLiftDefinitionId || ""}
      onChange={(e) =>
        patchPanelOverride({
          liftDefinitionId: e.target.value ? Number(e.target.value) : null,
          panelBehavior: e.target.value ? "door" : currentPanelBehavior,
          hardwareMode: e.target.value ? "lift" : currentHardwareMode,
        })
      }
      className="w-full px-2 py-1.5 border rounded text-sm bg-white"
    >
      <option value="">No lift</option>
      {liftDefinitions.map((def) => (
        <option key={def.id} value={def.id}>
          {def.name || def.code} · {def.category}
        </option>
      ))}
    </select>
  </div>

  <div>
  <label className="text-xs text-slate-500">Drawer runner / klizač</label>
  <select
    value={currentDrawerRunnerDefinitionId || ""}
    onChange={(e) => changeDrawerRunner(e.target.value)}
    className="w-full px-2 py-1.5 border rounded text-sm bg-white"
  >
    <option value="">No runner</option>

    {runnerDefinitions.map((def) => (
      <option key={def.id} value={def.id}>
        {def.name || def.code} · {def.category}
      </option>
    ))}
  </select>

  {currentDrawerRunnerDefinitionId ? (
  <button
    type="button"
    onClick={() =>
      window.open(
        `/interior-editor/hardware-definitions/${currentDrawerRunnerDefinitionId}/setup`,
        "_blank"
      )
    }
    className="mt-2 w-full px-2 py-1.5 border rounded bg-white text-xs text-blue-700 hover:bg-blue-50"
  >
    Advanced runner setup
  </button>
) : null}
</div>

{(currentPanelBehavior === "drawer" || currentHardwareMode === "drawer") ? (
  <div className="border rounded p-2 bg-slate-50 space-y-2">
    <div className="text-xs font-semibold text-slate-500">
      Drawer parameters
    </div>

    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-slate-500">Side clearance</label>
        <input
          type="number"
          value={drawerParams.sideClearance ?? ""}
onChange={(e) => updateDrawerParam("sideClearance", e.target.value)}
          className="w-full px-2 py-1 border rounded text-sm"
        />
      </div>

      <div>
        <label className="text-xs text-slate-500">Runner height</label>
        <input
  type="number"
  value={drawerParams.runnerHeight ?? ""}
  placeholder="40"
  onChange={(e) => updateDrawerParam("runnerHeight", e.target.value)}
  className="w-full px-2 py-1 border rounded text-sm"
/>
      </div>

      <div>
        <label className="text-xs text-slate-500">Runner length</label>
        <input
  type="number"
  value={drawerParams.runnerLength ?? ""}
  placeholder="300"
  onChange={(e) => updateDrawerParam("runnerLength", e.target.value)}
  className="w-full px-2 py-1 border rounded text-sm"
/>
      </div>

      <div>
        <label className="text-xs text-slate-500">Drawer box height</label>
        <input
          type="number"
          value={currentPanelOverride.drawerBoxHeight ?? ""}
          placeholder="120"
          onChange={(e) =>
            patchPanelOverride({
              drawerBoxHeight:
                e.target.value === "" ? null : Number(e.target.value),
            })
          }
          className="w-full px-2 py-1 border rounded text-sm"
        />
      </div>

      <div>
  <label className="text-xs text-slate-500">Offset left</label>
  <input
    type="number"
    value={currentPanelOverride.drawerOffsetLeft ?? ""}
    placeholder="16"
    onChange={(e) =>
      patchPanelOverride({
        drawerOffsetLeft:
          e.target.value === "" ? null : Number(e.target.value),
      })
    }
    className="w-full px-2 py-1 border rounded text-sm"
  />
</div>

<div>
  <label className="text-xs text-slate-500">Offset right</label>
  <input
    type="number"
    value={currentPanelOverride.drawerOffsetRight ?? ""}
    placeholder="16"
    onChange={(e) =>
      patchPanelOverride({
        drawerOffsetRight:
          e.target.value === "" ? null : Number(e.target.value),
      })
    }
    className="w-full px-2 py-1 border rounded text-sm"
  />
</div>

<div>
  <label className="text-xs text-slate-500">Offset bottom</label>
  <input
    type="number"
    value={currentPanelOverride.drawerOffsetBottom ?? ""}
    placeholder="12"
    onChange={(e) =>
      patchPanelOverride({
        drawerOffsetBottom:
          e.target.value === "" ? null : Number(e.target.value),
      })
    }
    className="w-full px-2 py-1 border rounded text-sm"
  />
</div>

<div>
  <label className="text-xs text-slate-500">Offset top</label>
  <input
    type="number"
    value={currentPanelOverride.drawerOffsetTop ?? ""}
    placeholder="20"
    onChange={(e) =>
      patchPanelOverride({
        drawerOffsetTop:
          e.target.value === "" ? null : Number(e.target.value),
      })
    }
    className="w-full px-2 py-1 border rounded text-sm"
  />
</div>

<div>
  <label className="text-xs text-slate-500">Side thickness</label>
  <input
    type="number"
    value={currentPanelOverride.drawerSideThickness ?? ""}
    placeholder="16"
    onChange={(e) =>
      patchPanelOverride({
        drawerSideThickness:
          e.target.value === "" ? null : Number(e.target.value),
      })
    }
    className="w-full px-2 py-1 border rounded text-sm"
  />
</div>

<div>
  <label className="text-xs text-slate-500">Bottom thickness</label>
  <input
    type="number"
    value={currentPanelOverride.drawerBottomThickness ?? ""}
    placeholder="18"
    onChange={(e) =>
      patchPanelOverride({
        drawerBottomThickness:
          e.target.value === "" ? null : Number(e.target.value),
      })
    }
    className="w-full px-2 py-1 border rounded text-sm"
  />
</div>

<div>
  <label className="text-xs text-slate-500">Bottom offset</label>
  <input
    type="number"
    value={currentPanelOverride.drawerBottomOffset ?? ""}
    placeholder="10"
    onChange={(e) =>
      patchPanelOverride({
        drawerBottomOffset:
          e.target.value === "" ? null : Number(e.target.value),
      })
    }
    className="w-full px-2 py-1 border rounded text-sm"
  />
</div>

<div>
  <label className="text-xs text-slate-500">Front/back thickness</label>
  <input
    type="number"
    value={currentPanelOverride.drawerFrontBackThickness ?? ""}
    placeholder="16"
    onChange={(e) =>
      patchPanelOverride({
        drawerFrontBackThickness:
          e.target.value === "" ? null : Number(e.target.value),
      })
    }
    className="w-full px-2 py-1 border rounded text-sm"
  />
</div>

<div>
  <label className="text-xs text-slate-500">Side drop below runner</label>
  <input
    type="number"
    value={currentPanelOverride.drawerSideDropBelowRunner ?? ""}
    placeholder="10"
    onChange={(e) =>
      patchPanelOverride({
        drawerSideDropBelowRunner:
          e.target.value === "" ? null : Number(e.target.value),
      })
    }
    className="w-full px-2 py-1 border rounded text-sm"
  />
</div>

<div>
  <label className="text-xs text-slate-500">Depth reduction</label>
  <input
  type="number"
  value={drawerParams.drawerDepthReduction ?? ""}
  placeholder="10"
  onChange={(e) => updateDrawerParam("drawerDepthReduction", e.target.value)}
  className="w-full px-2 py-1 border rounded text-sm"
/>
</div>
    </div>
  </div>
) : null}
</div>

      

      <div className="grid grid-cols-3 gap-2 pt-2 border-t">
        <div>
          <label className="text-xs text-slate-500">Width</label>
          <input
            type="number"
            value={overrides.width ?? ""}
            placeholder={String(effective.width ?? "")}
            onChange={(e) => updateDrawerParam("width", e.target.value)}
            className="w-full px-2 py-1 border rounded text-sm"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500">Height</label>
          <input
            type="number"
            value={overrides.height ?? ""}
            placeholder={String(effective.height ?? "")}
            onChange={(e) => updateDrawerParam("height", e.target.value)}
            className="w-full px-2 py-1 border rounded text-sm"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500">Depth / thickness</label>
          <input
            type="number"
            value={overrides.depth ?? overrides.thickness ?? ""}
            placeholder={String(effective.depth ?? effective.thickness ?? "")}
            onChange={(e) => {
              updateDrawerParam("depth", e.target.value);
              updateDrawerParam("thickness", e.target.value);
            }}
            className="w-full px-2 py-1 border rounded text-sm"
          />
        </div>
      </div>
    </div>
  );
}